<template lang="pug">
div.commentary-detail-chip
  VChip(
    :color="dark ? 'primary-c100' : 'primary-c700'"
    variant="outlined"
    @click="sheetOpen = true"
  ) {{ selectedDetailLevelName }}

  VBottomSheet(
    v-model="sheetOpen"
    style="max-width: 700px; margin-left: auto; margin-right: auto"
  )
    VList(:selected="selected")
      VListSubheader.text-h3 {{ i18n.t("Taxonomy.LevelOfDetail") }}
      VListItem(
        v-for="item in listItems"
        :key="item.uuid"
        :value="item.weight"
        @click="model = item.weight; sheetOpen = false"
      )
        template(#prepend)
          CommentaryDetailLevelDots.mr-4(
            v-if="item.nDots > 0"
            :length="detailLevels.length"
            :value="item.nDots"
            :name="item.name[locale] ?? '?'"
          )
          VIcon(v-else) {{ mdiClose }}
        VListItemTitle {{ item.name[locale] }}
        VListItemSubtitle
          span(
            v-html="i18n.t('Commentary.comments', { count: item.countHtml }, item.count)"
          )
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { mdiClose } from "@mdi/js";
import { usePreferencesStore } from "~/stores/usePreferencesStore";
import {
  detailLevels,
  getDefaultDetailLevel,
  getDetailLevelFromWeightWithFallback,
} from "~/static/taxonomy/detailLevels";
import { createCountHtml } from "./CommentaryType.util";
import { useDarkMode } from "~/composables/useDarkMode";

interface Props {
  /**
   * The "level of detail" taxonomy term's weight.
   */
  detailLevelWeight?: number;

  /**
   * A nested map of detail level counts, first keyed by language, then by detail level weights.
   * The values are the number of available comments for each level plus all levels below it.
   */
  counts: Map<string, Map<number, number>>;
}

const i18n = useI18n();
const locale = i18n.locale;
const dark = useDarkMode();

const preferencesStore = usePreferencesStore();

const otherLanguages = computed<string[]>(() =>
  preferencesStore.getOtherLanguages(unref(i18n.locale)),
);

const props = withDefaults(defineProps<Props>(), {
  detailLevelWeight: () => getDefaultDetailLevel().weight,
});

const sheetOpen = ref<boolean>(false);

const selected = computed(() => [props.detailLevelWeight]);

const emit = defineEmits<{
  "update:detailLevelWeight": [value: number];
}>();

const model = computed<number>({
  get() {
    return props.detailLevelWeight;
  },
  set(value) {
    emit("update:detailLevelWeight", value);
  },
});

const getCountData = (weight: number) => {
  const countLocal = props.counts.get(locale.value)?.get(weight) ?? 0;
  const maxCountOther = unref(otherLanguages).reduce(
    (acc: null | number, currentOtherLocale: string) => {
      const count = props.counts.get(currentOtherLocale)?.get(weight) ?? 0;
      return Math.max(count, acc ?? 0);
    },
    null,
  );
  return {
    countTotal: Math.max(countLocal, maxCountOther ?? 0),
    countLocal,
    maxCountOther,
    countHtml: createCountHtml({ countLocal, maxCountOther }),
  };
};

const listItems = computed(() => {
  return detailLevels
    .map((level, i) => {
      const { countHtml, countTotal } = getCountData(level.weight);

      return {
        name: level.name,
        uuid: level.uuid,
        weight: level.weight,
        nDots: i + 1,
        count: countTotal,
        countHtml,
      };
    })
    .reverse();
});

const selectedDetailLevelName = computed<string>(() => {
  return (
    getDetailLevelFromWeightWithFallback(model.value).name[locale.value] ?? ""
  );
});
</script>
